import { forwardRef, useState, useEffect, useCallback, Ref, RefAttributes, type JSX } from "react";

import { FieldValues, useController, useWatch } from "react-hook-form";

import { FormAmountFieldProps, SubscribedFormAmountFieldProps, BaseFormAmountFieldProps } from "./types";

import { FormTextField } from "@/forms-design-system/FormTextField";
import { useValidators } from "@/hooks/useValidators";

function FormAmountFieldInner<T extends FieldValues>(
  { control, rules, name, shouldUnregister, ...rest }: FormAmountFieldProps<T>,
  ref: Ref<HTMLInputElement>
) {
  return control && name ? (
    <SubscribedFormAmountField
      {...rest}
      ref={ref}
      control={control}
      rules={rules}
      name={name}
      shouldUnregister={shouldUnregister}
    />
  ) : (
    <BaseFormAmountField {...rest} name={name} ref={ref} />
  );
}

FormAmountFieldInner.displayName = "FormAmountField";

export const FormAmountField = forwardRef(FormAmountFieldInner) as <T extends FieldValues>(
  props: FormAmountFieldProps<T> & RefAttributes<HTMLInputElement>
) => JSX.Element;

function SubscribedFormAmountFieldInner<T extends FieldValues>(
  {
    control,
    defaultValue,
    name,
    onBlur,
    onChange,
    rules,
    shouldUnregister,
    inputRef,
    ...rest
  }: SubscribedFormAmountFieldProps<T>,
  ref: Ref<HTMLInputElement>
) {
  const validators = useValidators(rules);
  const watchedValue = useWatch({ control, name });
  const internalDefaultValue = defaultValue || watchedValue;

  const { field } = useController<T>({
    name,
    control,
    defaultValue: internalDefaultValue as never,
    rules: validators,
    shouldUnregister,
  });

  const handleChange = useCallback(
    (centsValue: number | null) => {
      onChange?.(centsValue);
      field.onChange(centsValue);
    },
    [field, onChange]
  );

  const handleBlur = useCallback(
    (event: React.FocusEvent<HTMLInputElement>) => {
      onBlur?.(event);
      field.onBlur();
    },
    [field, onBlur]
  );

  return (
    <BaseFormAmountField
      {...rest}
      value={field.value}
      inputRef={inputRef}
      ref={ref}
      onBlur={handleBlur}
      onChange={handleChange}
      data-test={rest?.["data-test"] || "form-amount-field"}
      name={name}
    />
  );
}

const SubscribedFormAmountField = forwardRef(SubscribedFormAmountFieldInner) as <T extends FieldValues>(
  props: SubscribedFormAmountFieldProps<T> & RefAttributes<HTMLInputElement>
) => JSX.Element;

const BaseFormAmountField = forwardRef<HTMLInputElement, BaseFormAmountFieldProps>(
  ({ value = null, onChange, hideAdornment = false, inputRef, ...rest }, ref) => {
    const [internalValue, setInternalValue] = useState<string | null>(numericValueToStringValue(value));

    useEffect(() => {
      if (stringValueToNumericValue(internalValue) !== value) {
        setInternalValue(numericValueToStringValue(value));
      }
    }, [value, internalValue]);

    return (
      <FormTextField
        {...rest}
        type="number"
        startAdornment={hideAdornment ? undefined : "$"}
        value={internalValue}
        onChange={(e) => {
          const newValue = e.target.value;
          setInternalValue(newValue);

          const parsedValue = stringValueToNumericValue(newValue);

          onChange?.(parsedValue);
        }}
        inputRef={inputRef}
        ref={ref}
      />
    );
  }
);

BaseFormAmountField.displayName = "BaseFormAmountField";

function numericValueToStringValue(value: number | null) {
  if (value === null || isNaN(value)) {
    return "";
  }

  return (value / 100).toString();
}

function stringValueToNumericValue(value: string | null) {
  if (value === null || value === "") {
    return null;
  }

  const parsedValue = parseFloat(value);

  if (isNaN(parsedValue)) {
    return null;
  }

  return parsedValue * 100;
}
